/**
 * Time zone helpers built on Intl only (no Temporal, no tz library). See
 * docs/design/add-sessions.md section 1. Wall dates are 'YYYY-MM-DD', wall times 'HH:MM',
 * instants are UTC epoch ms. Weekdays are ISO: 1 = Monday ... 7 = Sunday.
 */

export const DEFAULT_TZ = 'Europe/Athens';
export const LOCAL_DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export const LOCAL_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const MINUTE_MS = 60_000;
export const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;

export type WallAdjust = 'none' | 'gap_forward' | 'overlap_earlier';

export interface ZonedWall {
  date: string;
  time: string;
  /** ISO weekday, 1 = Monday, 7 = Sunday. */
  weekday: number;
  /** Offset from UTC in minutes (Athens summer: 180). */
  offsetMin: number;
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function fmt(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    fmtCache.set(tz, f);
  }
  return f;
}

interface Fields {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
}

function fieldsAt(utcMs: number, tz: string): Fields {
  const out: Fields = { y: 0, mo: 0, d: 0, h: 0, mi: 0, s: 0 };
  for (const p of fmt(tz).formatToParts(new Date(utcMs))) {
    const n = Number(p.value);
    if (p.type === 'year') out.y = n;
    else if (p.type === 'month') out.mo = n;
    else if (p.type === 'day') out.d = n;
    else if (p.type === 'hour') out.h = n === 24 ? 0 : n;
    else if (p.type === 'minute') out.mi = n;
    else if (p.type === 'second') out.s = n;
  }
  return out;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

function parseDate(date: string): [number, number, number] {
  if (!LOCAL_DATE_RE.test(date)) throw new Error(`Bad local date: ${date}`);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return [y, m, d];
}

function parseTime(time: string): [number, number] {
  if (!LOCAL_TIME_RE.test(time)) throw new Error(`Bad local time: ${time}`);
  const [h, m] = time.split(':').map(Number) as [number, number];
  return [h, m];
}

/** Offset of `tz` from UTC at an instant, in ms. */
export function tzOffsetMs(utcMs: number, tz: string): number {
  const f = fieldsAt(utcMs, tz);
  const asUtc = Date.UTC(f.y, f.mo - 1, f.d, f.h, f.mi, f.s);
  const truncated = Math.floor(utcMs / 1000) * 1000;
  return asUtc - truncated;
}

export function isValidTz(tz: string): boolean {
  if (typeof tz !== 'string' || tz.length === 0 || tz.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(0);
    return true;
  } catch {
    return false;
  }
}

/** ISO weekday (1..7) of a wall date. */
export function isoWeekday(date: string): number {
  const [y, m, d] = parseDate(date);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return wd === 0 ? 7 : wd;
}

export function utcToZonedWall(utcMs: number, tz: string): ZonedWall {
  const f = fieldsAt(utcMs, tz);
  const date = `${f.y}-${pad2(f.mo)}-${pad2(f.d)}`;
  return {
    date,
    time: `${pad2(f.h)}:${pad2(f.mi)}`,
    weekday: isoWeekday(date),
    offsetMin: Math.round(tzOffsetMs(utcMs, tz) / MINUTE_MS),
  };
}

/**
 * The UTC instant of a wall date and time in a zone.
 * A wall time inside a spring-forward gap moves forward by the gap ('gap_forward',
 * 03:30 becomes 04:30 in Athens). One that happens twice at fall-back resolves to the
 * earlier instant ('overlap_earlier').
 */
export function zonedWallToUtc(date: string, time: string, tz: string): { utcMs: number; adjusted: WallAdjust } {
  const [y, m, d] = parseDate(date);
  const [h, mi] = parseTime(time);
  const g = Date.UTC(y, m - 1, d, h, mi);
  const o1 = tzOffsetMs(g - 12 * HOUR_MS, tz);
  const o2 = tzOffsetMs(g + 12 * HOUR_MS, tz);
  const c1 = g - o1;
  const c2 = g - o2;
  const valid = (c: number) => {
    const w = utcToZonedWall(c, tz);
    return w.date === date && w.time === time;
  };
  const v1 = valid(c1);
  const v2 = c2 !== c1 && valid(c2);
  if (v1 && v2) return { utcMs: Math.min(c1, c2), adjusted: 'overlap_earlier' };
  if (v1) return { utcMs: c1, adjusted: 'none' };
  if (v2) return { utcMs: c2, adjusted: 'none' };
  return { utcMs: g - o1, adjusted: 'gap_forward' };
}

/** Add n calendar days to a wall date. */
export function addLocalDays(date: string, n: number): string {
  const [y, m, d] = parseDate(date);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/** Whole days from a to b (b - a) for wall dates. */
export function diffLocalDays(a: string, b: string): number {
  const [ya, ma, da] = parseDate(a);
  const [yb, mb, db] = parseDate(b);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / DAY_MS);
}

/** [startUtc, endUtc) of a local day. 23 or 25 hours long on DST days. */
export function localDayRange(date: string, tz: string): [number, number] {
  return [zonedWallToUtc(date, '00:00', tz).utcMs, zonedWallToUtc(addLocalDays(date, 1), '00:00', tz).utcMs];
}

/** The local date of an instant ('YYYY-MM-DD'). */
export function localDateOf(utcMs: number, tz: string): string {
  return utcToZonedWall(utcMs, tz).date;
}

/** 'HH:MM' to minutes after midnight. */
export function minutesOfDay(time: string): number {
  const [h, m] = parseTime(time);
  return h * 60 + m;
}

/**
 * True when the instant falls inside a daily local window [start, end). A window that
 * wraps midnight ('23:00' to '08:00') works. start === end means an empty window.
 */
export function isWithinLocalWindow(utcMs: number, tz: string, start: string, end: string): boolean {
  const w = utcToZonedWall(utcMs, tz);
  const now = minutesOfDay(w.time);
  const s = minutesOfDay(start);
  const e = minutesOfDay(end);
  if (s === e) return false;
  return s < e ? now >= s && now < e : now >= s || now < e;
}

/**
 * The next instant strictly after `fromUtc` whose local wall time is `time` (for jobs that
 * run daily at '03:30' and for pushing a deferred notification to the end of quiet hours).
 */
export function nextLocalTime(fromUtc: number, time: string, tz: string): number {
  let date = localDateOf(fromUtc, tz);
  for (let i = 0; i < 3; i++) {
    const { utcMs } = zonedWallToUtc(date, time, tz);
    if (utcMs > fromUtc) return utcMs;
    date = addLocalDays(date, 1);
  }
  return zonedWallToUtc(date, time, tz).utcMs;
}
